import { Copy, Paperclip, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { prepareSample, type SampleUpload } from '../lib/samples';
import type { Child, EvaluatorLink, ShareSection, TestResult } from '../lib/types';
import { GradeSelect } from '../ui/GradeSelect';
import { useParent } from './ParentContext';

/**
 * Tests and evaluations, and sharing with an evaluator (#612, phase 5 of #453), on the Portfolio tab.
 */

const fmt = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const ord = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')}`;
const today = () => new Date().toLocaleDateString('en-CA');

interface Draft {
  id?: string;
  kind: 'test' | 'evaluation';
  name: string;
  date: string;
  grade: string;
  givenBy: string;
  scores: { subject: string; score: string; percentile: string }[];
  note: string;
  file: SampleUpload | null | undefined; // undefined: keep what's there
  fileName: string;
}
const blank = (grade: string): Draft => ({ kind: 'test', name: '', date: today(), grade, givenBy: '', scores: [{ subject: '', score: '', percentile: '' }], note: '', file: undefined, fileName: '' });

export function TestsCard({ child }: { child: Child }) {
  const { toast } = useParent();
  const [data, setData] = useState<{ enabled: boolean; tests: TestResult[] } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api
      .get<{ enabled: boolean; tests: TestResult[] }>(`/api/children/${child.id}/tests`)
      .then(setData)
      .catch(() => setData(null));
  }, [child.id]);
  useEffect(load, [load]);
  if (!data?.enabled) return null;

  const edit = (t: TestResult): Draft => ({
    id: t.id,
    kind: t.kind,
    name: t.name,
    date: t.date,
    grade: t.grade,
    givenBy: t.givenBy,
    scores: t.scores.length ? t.scores.map((s) => ({ subject: s.subject, score: s.score, percentile: s.percentile === null ? '' : String(s.percentile) })) : [{ subject: '', score: '', percentile: '' }],
    note: t.note,
    file: undefined,
    fileName: t.file?.name ?? '',
  });
  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      const body = {
        kind: draft.kind,
        name: draft.name,
        date: draft.date,
        grade: draft.grade,
        givenBy: draft.givenBy,
        note: draft.note,
        scores: draft.scores
          .filter((s) => s.subject.trim() || s.score.trim() || s.percentile.trim())
          .map((s) => ({ subject: s.subject.trim(), score: s.score.trim(), percentile: s.percentile.trim() ? Math.min(99, Math.max(1, Math.round(Number(s.percentile)))) : null })),
        ...(draft.file !== undefined ? { file: draft.file ? { name: draft.file.name, data: draft.file.data } : null } : {}),
      };
      if (draft.id) await api.put(`/api/children/${child.id}/tests/${draft.id}`, body);
      else await api.post(`/api/children/${child.id}/tests`, body);
      setDraft(null);
      load();
      toast({ text: 'Saved. It’s in the portfolio and any evaluator link.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function remove(t: TestResult) {
    if (!confirm(`Remove ${t.name} (${fmt(t.date)})?`)) return;
    try {
      await api.del(`/api/children/${child.id}/tests/${t.id}`);
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const set = (patch: Partial<Draft>) => draft && setDraft({ ...draft, ...patch });
  const setScore = (i: number, patch: Partial<Draft['scores'][number]>) => draft && set({ scores: draft.scores.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  return (
    <section className="card stack tests-card">
      <div className="row-between wrap">
        <h2>Tests and evaluations</h2>
        <button className="btn small" onClick={() => setDraft(draft ? null : blank(child.grade ?? ''))}>
          {draft ? 'Cancel' : (
            <>
              <Plus size={15} /> Add a result
            </>
          )}
        </button>
      </div>
      <p className="muted small">Standardized tests and evaluations, with the scanned report if you have it. They go in {child.name}’s portfolio and any evaluator link.</p>
      {draft && (
        <div className="test-form stack">
          <div className="field-row wrap-row">
            <label>
              Kind
              <select value={draft.kind} onChange={(e) => set({ kind: e.target.value as Draft['kind'] })}>
                <option value="test">Standardized test</option>
                <option value="evaluation">Evaluation</option>
              </select>
            </label>
            <label className="grow">
              Name
              <input value={draft.name} onChange={(e) => set({ name: e.target.value })} maxLength={120} placeholder={draft.kind === 'test' ? 'e.g. Iowa Assessments' : 'e.g. Annual evaluation'} />
            </label>
            <label>
              Date
              <input type="date" value={draft.date} onChange={(e) => set({ date: e.target.value })} />
            </label>
            <label>
              Grade
              <GradeSelect className="doc-grade" value={draft.grade} onChange={(grade) => set({ grade })} />
            </label>
          </div>
          <label>
            {draft.kind === 'test' ? 'Given by' : 'Evaluator'}
            <input value={draft.givenBy} onChange={(e) => set({ givenBy: e.target.value })} maxLength={120} placeholder={draft.kind === 'test' ? 'e.g. Co-op testing day' : 'e.g. Ms. Dana Carter, certified teacher'} />
          </label>
          <div className="score-rows">
            <span className="field-label">Subject</span>
            <span className="field-label">Score</span>
            <span className="field-label">Percentile</span>
            <span />
            {draft.scores.map((s, i) => (
              <ScoreRow key={i} s={s} onChange={(p) => setScore(i, p)} onRemove={() => set({ scores: draft.scores.length > 1 ? draft.scores.filter((_, j) => j !== i) : [{ subject: '', score: '', percentile: '' }] })} />
            ))}
          </div>
          <button type="button" className="link-btn" onClick={() => set({ scores: [...draft.scores, { subject: '', score: '', percentile: '' }] })}>
            <Plus size={14} /> Add a subject
          </button>
          <label>
            {draft.kind === 'test' ? 'Result or note' : 'The evaluator’s finding'}
            <textarea rows={2} value={draft.note} onChange={(e) => set({ note: e.target.value })} maxLength={2000} placeholder="e.g. Making adequate progress" />
          </label>
          <div className="row-between wrap">
            <label className="btn small file-pick">
              <Paperclip size={15} /> {draft.file ? draft.file.name : draft.file === null || !draft.fileName ? 'Attach the report (PDF or photo)' : `Replace ${draft.fileName}`}
              <input
                type="file"
                accept="application/pdf,image/*"
                hidden
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (!f) return;
                  try {
                    set({ file: await prepareSample(f, 25) });
                  } catch (err) {
                    toast({ text: (err as Error).message, tone: 'alert' });
                  }
                }}
              />
            </label>
            {(draft.file || (draft.fileName && draft.file !== null)) && (
              <button type="button" className="link-btn small" onClick={() => set({ file: null, fileName: '' })}>
                Remove the report
              </button>
            )}
            <button className="btn primary" disabled={busy || !draft.name.trim() || !draft.date} onClick={save}>
              Save
            </button>
          </div>
        </div>
      )}
      {!data.tests.length && !draft && <p className="muted">Nothing recorded yet.</p>}
      {data.tests.map((t) => (
        <div key={t.id} className="test-row stack">
          <div className="row-between wrap">
            <div className="stack tight">
              <strong>{t.name}</strong>
              <span className="muted small">
                {[t.kind === 'test' ? 'Standardized test' : 'Evaluation', fmt(t.date), t.grade && `${t.grade}${/grade/i.test(t.grade) ? '' : ' grade'}`, t.givenBy].filter(Boolean).join(' · ')}
              </span>
            </div>
            <div className="row-gap">
              {t.file && (
                <a className="btn small" href={`/api/children/${child.id}/tests/${t.id}/file`} onClick={(e) => openFile(e, `/api/children/${child.id}/tests/${t.id}/file`)}>
                  <Paperclip size={14} /> {t.file.name}
                </a>
              )}
              <button className="btn small" onClick={() => setDraft(edit(t))}>
                Edit
              </button>
              <button className="icon-btn" aria-label={`Remove ${t.name}`} onClick={() => remove(t)}>
                <Trash2 size={15} />
              </button>
            </div>
          </div>
          {!!t.scores.length && (
            <div className="score-chips">
              {t.scores.map((s, i) => (
                <span key={i} className="split-chip">
                  {[s.subject, s.score, s.percentile !== null && `${ord(s.percentile)} percentile`].filter(Boolean).join(' · ')}
                </span>
              ))}
            </div>
          )}
          {t.note && <p className="test-note">“{t.note}”</p>}
        </div>
      ))}
    </section>
  );
}

function ScoreRow({ s, onChange, onRemove }: { s: Draft['scores'][number]; onChange: (p: Partial<Draft['scores'][number]>) => void; onRemove: () => void }) {
  return (
    <>
      <input value={s.subject} onChange={(e) => onChange({ subject: e.target.value })} maxLength={60} placeholder="Reading" aria-label="Subject" />
      <input value={s.score} onChange={(e) => onChange({ score: e.target.value })} maxLength={30} placeholder="214" aria-label="Score" />
      <input type="number" min={1} max={99} value={s.percentile} onChange={(e) => onChange({ percentile: e.target.value })} placeholder="81" aria-label="Percentile" />
      <button type="button" className="icon-btn" aria-label="Remove this subject" onClick={onRemove}>
        <X size={15} />
      </button>
    </>
  );
}

/** Files behind the parent's sign-in open through fetch, so the token goes along. */
async function openFile(e: React.MouseEvent, url: string) {
  e.preventDefault();
  const { tokens } = await import('../lib/api');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${tokens.parent()}` } });
  if (!res.ok) return;
  const blobUrl = URL.createObjectURL(await res.blob());
  window.open(blobUrl, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

const SHARE: { id: ShareSection; label: string; on: boolean }[] = [
  { id: 'summary', label: 'State requirement summary', on: true },
  { id: 'attendance', label: 'Attendance and hours by subject', on: true },
  { id: 'grades', label: 'Grades', on: true },
  { id: 'reading', label: 'Reading list', on: true },
  { id: 'samples', label: 'Work samples', on: true },
  { id: 'tests', label: 'Tests and evaluations', on: true },
  { id: 'trips', label: 'Field trips', on: true },
  { id: 'journal', label: 'Homeschool journal', on: false },
  { id: 'pdf', label: 'Download the portfolio PDF', on: true },
];

export function EvaluatorLinks({ child }: { child: Child }) {
  const { toast } = useParent();
  const [data, setData] = useState<{ enabled: boolean; maxDays: number; range: { from: string; to: string }; links: EvaluatorLink[] } | null>(null);
  const [label, setLabel] = useState('');
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(30);
  const [on, setOn] = useState<Set<ShareSection>>(new Set(SHARE.filter((s) => s.on).map((s) => s.id)));
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api
      .get<{ enabled: boolean; maxDays: number; range: { from: string; to: string }; links: EvaluatorLink[] }>(`/api/children/${child.id}/evaluator-links`)
      .then(setData)
      .catch(() => setData(null));
  }, [child.id]);
  useEffect(load, [load]);
  if (!data?.enabled) return null;
  const choices = [7, 14, 30, 60, 90, 180, 365].filter((d) => d <= data.maxDays);

  async function make() {
    if (!data) return;
    setBusy(true);
    try {
      const r = await api.post<EvaluatorLink & { emailed: boolean }>(`/api/children/${child.id}/evaluator-links`, {
        label,
        email,
        days: Math.min(days, data.maxDays),
        from: data.range.from,
        to: data.range.to,
        sections: [...on],
      });
      await navigator.clipboard?.writeText(r.url).catch(() => undefined);
      toast({ text: r.emailed ? `Link made and emailed to ${email}. It’s copied too.` : 'Link made and copied. Send it to your evaluator.', tone: 'good' });
      setLabel('');
      setEmail('');
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function off(l: EvaluatorLink) {
    if (!confirm(`Turn off ${l.label}’s link? It stops working right away.`)) return;
    try {
      await api.del(`/api/children/${child.id}/evaluator-links/${l.id}`);
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <section className="card stack evaluator-links">
      <h2>Share with an evaluator</h2>
      <p className="muted small">
        A read-only link to {child.name}’s school year ({fmt(data.range.from)} – {fmt(data.range.to)}) for your evaluator or certified teacher. They don’t need an account and can’t
        change anything. Turn it off any time.
      </p>
      <div className="field-row wrap-row">
        <label className="grow">
          Who it’s for
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="e.g. Ms. Dana Carter" />
        </label>
        <label>
          Works for
          <select value={Math.min(days, data.maxDays)} onChange={(e) => setDays(Number(e.target.value))}>
            {choices.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
            {!choices.includes(data.maxDays) && <option value={data.maxDays}>{data.maxDays} days</option>}
          </select>
        </label>
      </div>
      <label>
        Their email <span className="muted small">(optional: we email them the link)</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
      </label>
      <div className="share-sections" role="group" aria-label="What they see">
        {SHARE.map((s) => (
          <label key={s.id} className="check compact">
            <input
              type="checkbox"
              checked={on.has(s.id)}
              onChange={(e) => {
                const next = new Set(on);
                if (e.target.checked) next.add(s.id);
                else next.delete(s.id);
                setOn(next);
              }}
            />
            <span>{s.label}</span>
          </label>
        ))}
      </div>
      <div>
        <button className="btn primary" disabled={busy || !label.trim() || !on.size} onClick={make}>
          Make a link
        </button>
      </div>
      {!!data.links.length && (
        <div className="stack">
          <h3>Links</h3>
          {data.links.map((l) => (
            <div key={l.id} className={`share-link ${l.active ? '' : 'expired'}`}>
              <div className="row-between wrap">
                <div className="stack tight">
                  <strong>{l.label}</strong>
                  <span className="muted small">
                    {l.views ? `Opened ${l.views} time${l.views === 1 ? '' : 's'} · last ${new Date(l.lastViewedAt!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : 'Not opened yet'} ·{' '}
                    {l.active ? `works until ${new Date(l.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : 'expired'}
                    {l.email && ` · ${l.email}`}
                  </span>
                </div>
                <div className="row-gap">
                  {l.active && (
                    <button className="btn small" onClick={() => navigator.clipboard?.writeText(l.url).then(() => toast({ text: 'Link copied.', tone: 'good' }))}>
                      <Copy size={14} /> Copy link
                    </button>
                  )}
                  <button className="btn small danger" onClick={() => off(l)}>
                    {l.active ? 'Turn off' : 'Remove'}
                  </button>
                </div>
              </div>
              {l.active && <span className="mono small share-url">{l.url}</span>}
            </div>
          ))}
          <p className="muted small">You’re told the first time a link is opened.</p>
        </div>
      )}
    </section>
  );
}

